
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, ArrowRight } from "lucide-react";
import { Group } from "@/types/group";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { safeSelectArray, safeCast } from "@/utils/supabaseHelpers";
import { supabase } from "@/integrations/supabase/client";

interface GroupCardProps {
  group: Group;
  onClick: (group: Group) => void;
}

interface MemberAvatar {
  avatar_url: string | null;
  name: string;
}

export const GroupCard = ({ group, onClick }: GroupCardProps) => {
  const { i18n, t } = useTranslation();
  const [memberAvatars, setMemberAvatars] = useState<MemberAvatar[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expenseCount, setExpenseCount] = useState<number>(0);
  const [totalMemberCount, setTotalMemberCount] = useState<number>(0);
  
  const formatDate = () => {
    if (!group.created_at) {
      console.log(`[GroupCard] No created_at for group ${group.id}`);
      return "-";
    }
    
    try {
      const date = new Date(group.created_at);
      
      if (isNaN(date.getTime())) {
        console.log(`[GroupCard] Invalid date for group ${group.id}: ${group.created_at}`);
        return "-";
      }
      
      console.log(`[GroupCard] Formatting date for group ${group.id}: ${date.toString()}`);
      
      if (i18n.language === 'ja') {
        return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
      } else {
        return date.toLocaleDateString();
      }
    } catch (error) {
      console.error(`[GroupCard] Error formatting date for group ${group.id}:`, error);
      return "-";
    }
  };
  
  const dateString = formatDate();
  
  console.log(`[GroupCard] Rendering group ${group.id} (${group.name}):`, {
    created_at: group.created_at,
    dateString: dateString,
    languageContext: i18n.language
  });

  useEffect(() => {
    const fetchMemberAvatars = async () => {
      try {
        setIsLoading(true);
        
        const response = await safeSelectArray<any>(
          'group_members',
          { group_id: group.id },
          { 
            select: `
              profiles (
                avatar_url,
                name
              )
            `,
            limit: 5
          }
        );

        if (response.success && response.data) {
          const avatars: MemberAvatar[] = [];
          for (const member of response.data) {
            const memberData = safeCast<any>(member, {});
            if (memberData?.profiles) {
              avatars.push({
                avatar_url: memberData.profiles.avatar_url,
                name: memberData.profiles.name || 'Unknown'
              });
            }
          }
          
          setMemberAvatars(avatars);
        } else {
          console.error("Error fetching member avatars:", response.error);
          setMemberAvatars([]);
        }

        const countResponse = await supabase
          .from('group_members')
          .select('id', { count: 'exact' })
          .eq('group_id', group.id);
          
        if (countResponse.count !== null) {
          console.log(`Group ${group.id} (${group.name}) has ${countResponse.count} members`);
          setTotalMemberCount(countResponse.count);
        } else {
          setTotalMemberCount(group.member_count || memberAvatars.length);
        }
        
      } catch (error) {
        console.error("Error fetching member avatars:", error);
        setMemberAvatars([]);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchExpenseCount = async () => {
      if (!group.id) return;
      
      try {
        const { data, error, count } = await supabase
          .from('expenses')
          .select('id', { count: 'exact' })
          .eq('group_id', group.id);
        
        if (error) {
          console.error("Error fetching expense count:", error);
          return;
        }
        
        if (data) {
          const actualCount = count !== null ? count : data.length;
          console.log(`Group ${group.id} (${group.name}) has ${actualCount} expenses`);
          setExpenseCount(actualCount);
        } else {
          console.log(`No expenses found for group ${group.id} (${group.name})`);
          setExpenseCount(0);
        }
      } catch (error) {
        console.error("Error fetching expense count:", error);
      }
    };

    if (group.id) {
      fetchMemberAvatars();
      fetchExpenseCount();
    }
  }, [group.id, group.name, group.member_count]);

  const displayExpenseCount = group.total_expenses !== undefined ? 
    group.total_expenses : 
    expenseCount;
  
  const extraMembersCount = totalMemberCount > 5 ? totalMemberCount - 5 : 0;
  
  return (
    <Card
      className="group hover:shadow-lg transition-all duration-300 cursor-pointer backdrop-blur-sm bg-white/50 dark:bg-black/20 border-gray-200/50 dark:border-gray-800/50"
      onClick={() => onClick(group)}
    >
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-full bg-gradient-to-r from-primary to-green/80 bg-opacity-10 group-hover:bg-opacity-20 transition-colors">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{group.name}</h3>
              {group.is_new && (
                <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                  {t('common.new')}
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-500 block">
              {dateString}
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-primary transition-colors" />
        </div>
        <div className="flex justify-between items-center">
          <div className="flex -space-x-2">
            {isLoading ? (
              <>
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"></div>
              </>
            ) : (
              <>
                {memberAvatars.map((member, index) => (
                  <Avatar key={index} className="border-2 border-background w-8 h-8">
                    <AvatarImage src={member.avatar_url || undefined} alt={member.name} />
                    <AvatarFallback className="bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      {member.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {extraMembersCount > 0 && (
                  <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-medium border-2 border-background text-gray-600 dark:text-gray-300">
                    +{extraMembersCount}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="text-sm text-gray-500">
            {t('groups.expenses')} {displayExpenseCount}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
